import type { ReactElement } from 'react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { get, post } from 'utils/network';
import { ApiRoute, ErrorMessage } from 'config';

import BillingAddress from 'models/BillingAddress';
import type { AddressErrors, IBasicAddress, ZipInfo } from 'models/Address';
import { State } from 'models/Address';

import { Component as CaretIcon } from 'icons/icon-ui--caret.svg';
import ValidableControl from 'components/shared/ValidableControl';
import Banner from 'components/shared/Banner';
import useCurrentUser from '../../hooks/useCurrentUser';

type Props = {
  address?: BillingAddress;
  userID?: number;
  submitButtonLabel?: string;
  onSuccess: () => void;
  hideSubmitButton?: boolean;
  compactDesign?: boolean;
};

enum ComponentLengthType {
  STREET_NUMBER = 'short_name',
  ROUTE = 'long_name',
}

const MAX_ADDRESS2_LENGTH = 30;

// TODO: consolidate duplicate address form components/logic into one (AddressView.tsx)
// eslint-disable-next-line react/display-name
const AddressView = forwardRef(({
  address,
  userID,
  submitButtonLabel = 'Next',
  onSuccess,
  hideSubmitButton = false,
  compactDesign = false,
}: Props, ref): ReactElement => {
  const isAccountManagementEnabled = window.gon?.featureFlags?.isAccountManagementEnabled;
  const getAttr = (attr) => (address ? address[attr] : '');
  const [isLoading, setLoading] = useState(false);
  const { currentUser } = useCurrentUser();
  const [streetAddress, setStreetAddress] = useState(getAttr('address') || '');
  const [apartment, setApartment] = useState(getAttr('address2') || '');
  const [zip, setZip] = useState(getAttr('zip') || '');
  const [state, setState] = useState(getAttr('state') || '');
  const [city, setCity] = useState(getAttr('city') || '');
  const [canSubmit, setCanSubmit] = useState(false);

  const addressInputRef = useRef();
  const [errors, setErrors] = useState({});
  const getErrorMsg = (attr) => (errors[attr] ? errors[attr][0] : '');
  const isInvalid = (attr) => !!errors[attr];

  const submitButtonRef = useRef();
  const [addressSaved, setAddressSaved] = useState(false);

  useEffect(() => {
    (async () => {
      if (zip?.length === 5) {
        const response = await get<ZipInfo>(`/zip_codes/${zip}`);

        if (response.ok) {
          const stateCode = response?.parsedBody?.state_code;
          const cityName = response?.parsedBody?.city;

          if (stateCode && cityName) {
            setState(stateCode);
            setCity(cityName);
          }
        }
      }
    })();
  }, [zip]);

  useEffect(() => {
    const isAddress2Valid = !(apartment?.length > MAX_ADDRESS2_LENGTH);
    setCanSubmit(
      !!streetAddress && !!zip && !!state && !!city && !!userID
      && isAddress2Valid,
    );
  }, [streetAddress, state, zip, city, userID, apartment]);

  useEffect(() => {
    if (window.google) {
      const inputElement = addressInputRef.current;
      const autoComplete = new window.google.maps.places.Autocomplete(
        // @ts-expect-error TODO: fix null safety
        (inputElement),
        {
          types: ['geocode'],
          componentRestrictions: { country: 'us' },
        },
      );

      autoComplete.addListener('place_changed', () => {
        const components = autoComplete.getPlace()?.address_components;

        if (components === undefined) return;

        setStreetAddress([
          components[0][ComponentLengthType.STREET_NUMBER],
          components[1][ComponentLengthType.ROUTE],
        ].join(' '));

        setState('');
        setCity('');
        setZip('');

        for (let i = 0; i < components.length; i++) {
          const addressType = components[i].types[0];
          const addressLengthType = ComponentLengthType[addressType.toUpperCase()] || 'short_name';

          if (addressType !== 'street_number' && addressType !== 'route') {
            const val = components[i][addressLengthType];

            if (addressType === 'administrative_area_level_1') {
              setState(val);
            } else if (addressType === 'locality') {
              setCity(val);
            } else if (addressType === 'postal_code') {
              setZip(val);
            }
          }
        }
      });
    }
  }, []);

  useEffect(() => {
    if (addressSaved) {
      const timer = setTimeout(() => {
        setAddressSaved(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [addressSaved]);

  const handleSubmit = async () => {
    const payload = {
      billing_address: {
        address: streetAddress,
        address2: apartment,
        city,
        state,
        zip,
      } as IBasicAddress,
    };

    setLoading(true);

    const response = await post(`${ApiRoute.USERS}/${userID}/billing_address`, payload);

    setLoading(false);

    if (response.status === 422) {
      // @ts-expect-error TODO: fix null safety
      setErrors(response.parsedBody);
    } else {
      setErrors({});
      onSuccess();
      setAddressSaved(true);
    }
  };

  useEffect(() => {
    if (apartment?.length && apartment.length > MAX_ADDRESS2_LENGTH) {
      setErrors((lastErrors) => ({
        ...lastErrors,
        address2: [ErrorMessage.CHAR_LIMIT_ADDRESS2],
      }));
    } else {
      setErrors((lastErrors) => {
        const newErrors = { ...lastErrors } as AddressErrors;
        delete newErrors.address2;
        return newErrors;
      });
    }
  }, [apartment]);

  const handleApartmentChange = (value) => {
    setApartment((lastValue) => {
      if (value.length <= MAX_ADDRESS2_LENGTH) return value;

      return value.length < lastValue.length
        ? value
        : lastValue;
    });
  };

  useImperativeHandle(ref, () => ({
    handleSubmit,
  }));

  const inlineFieldsClassName = `col-xs-${compactDesign ? 6 : 4} col-12 mt-2`;

  return (
    <div className="row">
      {isInvalid('base') && (
        <div className="mb-4 col-12 mt-2 u-text--sm">
          <div className="c-alert c-alert--error" role="alert">
            <div className="c-alert__text">
              {getErrorMsg('base')}
            </div>
          </div>
        </div>
      )}
      <div className="col-12">
        <ValidableControl
          label="Street Address *"
          htmlFor="add-address-address"
          isInvalid={isInvalid('address')}
          errorMessage={getErrorMsg('address')}
        >
          <input
            id="add-address-address"
            aria-label="add billing address address"
            // @ts-expect-error TODO: fix null safety
            ref={addressInputRef}
            placeholder="123 Sample Street"
            type="text"
            value={streetAddress}
            onChange={(e) => setStreetAddress(e.target.value)}
          />
        </ValidableControl>
      </div>
      <div className="col-12 mt-2">
        <ValidableControl
          label={(
            <>
              Apartment/Suite&nbsp;
              <span>(optional)</span>
            </>
          )}
          htmlFor="add-address-address2"
          isInvalid={isInvalid('address2')}
          invalidClassName={apartment?.length > MAX_ADDRESS2_LENGTH
            ? 'is-invalid'
            : 'has-warning'}
          errorMessage={getErrorMsg('address2')}
        >
          <input
            id="add-address-address2"
            aria-label="add billing address address 2"
            placeholder="XYZ Business, Suite 123"
            type="text"
            value={apartment}
            onChange={(e) => handleApartmentChange(e.target.value)}
          />
        </ValidableControl>
      </div>
      <div className={inlineFieldsClassName}>
        <ValidableControl
          label="Zipcode *"
          htmlFor="add-address-zip"
          isInvalid={isInvalid('zip')}
          errorMessage={getErrorMsg('zip')}
        >
          <input
            id="add-address-zip"
            aria-label="add billing address zip code"
            placeholder="12345"
            type="text"
            pattern="\d*"
            inputMode="numeric"
            maxLength={5}
            value={zip}
            onChange={(e) => setZip(e.target.value)}
          />
        </ValidableControl>
      </div>
      <div className={inlineFieldsClassName}>
        <ValidableControl
          label="State *"
          htmlFor="add-address-state"
          isInvalid={isInvalid('state')}
          errorMessage={getErrorMsg('state')}
        >
          <div className="c-input-group c-input-group--icon-right">
            <select
              id="add-address-state"
              aria-label="add billing address state"
              value={state}
              onChange={(e) => setState(e.target.value)}
            >
              <option value="" label="Select">&nbsp;</option>
              {Object.entries(State)
                .map(([key, val]) => (
                  <option key={key} value={key}>{val}</option>
                ))}
            </select>
            <button
              type="button"
              className="c-link c-input-icon c-input-icon--right"
            >
              <CaretIcon
                className="c-select__caret-icon c-svg-fill-gray-115-color"
                aria-label="expand state dropdown"
                height="12"
                width="12"
              />
            </button>
          </div>
        </ValidableControl>
      </div>
      <div className={inlineFieldsClassName}>
        <ValidableControl
          label="City *"
          htmlFor="add-address-city"
          isInvalid={isInvalid('city')}
          errorMessage={getErrorMsg('city')}
        >
          <input
            id="add-address-city"
            aria-label="add billing address city"
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
          />
        </ValidableControl>
      </div>
      {!hideSubmitButton && (
        <div className="col-12">
          <div
            className={['c-form-group', isAccountManagementEnabled ? 'c-my-account__payment-method__button-container' : ''].join(' ')}
          >
            <button
              // @ts-expect-error TODO: fix null safety
              ref={submitButtonRef}
              type="button"
              aria-label="submit billing address"
              disabled={isLoading || !canSubmit || currentUser.isDeactivated}
              className={[
                'c-button',
                'c-button--primary',
                'd-block',
                'd-md-inline-block',
                isAccountManagementEnabled ? 'p-3' : '',
              ].join(' ')}
              onClick={handleSubmit}
            >
              {submitButtonLabel}
            </button>
          </div>
          {addressSaved
            && (
              <div style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
              >
                <div>
                  <Banner.Success isCentered>
                    <div className="u-text--center">
                      Address updates saved!
                    </div>
                  </Banner.Success>
                </div>
              </div>
            )}
        </div>
      )}
    </div>
  );
});

export default AddressView;
